import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Typography, Switch, SearchBar, Image } from 'lib/components';
import { useAppDispatch, actions, useSelectTopNavState, useSelectCurrentUser } from 'store';
import { useWindowResize } from 'hooks/useWindowResize';
import classes from './index.module.scss';

/**
 * TopNav component
 * Renders the top navigation bar with logo, content header, search, and user info
 * Adapts to different screen sizes and routes
 *
 * @returns {JSX.Element} - Rendered component
 */
const TopNav = () => {
	// Router history for navigation
	const { push } = useHistory();
	// Redux state for top nav configuration
	const { contentHeader, mapToggleVisible, searchBarVisible, notificationVisible } = useSelectTopNavState();
	// Current user information
	const me = useSelectCurrentUser();
	// Redux dispatch for actions
	const dispatch = useAppDispatch();
	// Responsive design helpers
	const { isSmallDevice } = useWindowResize();

	// Search query state
	const [query, setQuery] = useState('');

	// Router history for back navigation and current path
	const {
		goBack,
		location: { pathname },
	} = useHistory();

	/**
	 * Open the side navigation menu on mobile
	 * Dispatches action to update Redux state
	 */
	const openMenu = () => {
		dispatch(actions.sidenavToggled(true));
	};

	/**
	 * Determine whether to hide the back button
	 * Back button is hidden on main/root pages
	 */
	const hideBackIcon =
		pathname === '/dashboard' ||
		pathname === '/nurses' ||
		pathname === '/jobs' ||
		pathname === '/invoices' ||
		pathname === '/billing' ||
		pathname === '/inventories' ||
		pathname === '/inventory/home' ||
		pathname === '/inventory/vendors' ||
		pathname === '/admins' ||
		pathname === '/hospitals';

	return (
		<div className={classes['topNav']}>
			<div className={classes['topNav-container']}>
				{/* Logo/brand section */}
				<div className={classes['topNav-logo']}>
					<Link to={'/'}>LOGO</Link>
				</div>

				{/* Main content section */}
				<div className={classes['topNav-content']}>
					{/* Page header with title and back button */}
					{contentHeader && (
						<div className={classes['topNav-content-header']}>
							{/* Back button - only shown on non-root pages */}
							{!hideBackIcon && (
								<a onClick={goBack} className={classes['topNav-content-header-back-btn']}>
									<Image src={'icLeftCaret'} />
								</a>
							)}
							{/* Page title */}
							<Typography variant="h3">{contentHeader}</Typography>
						</div>
					)}

					{/* Map toggle - only shown on dashboard */}
					{pathname === '/dashboard' && (
						<div className={classes['topNav-content-mapToggle']}>
							<Typography className={classes['topNav-content-mapToggle-title']}>
								Toggle Map View:
							</Typography>
							<div>
								<Switch
									id={`topNav-content-mapToggle-switch`}
									checked={mapToggleVisible}
									handleChecked={(value) => dispatch(actions.mapDisplayToggled(value))}
								/>
							</div>
						</div>
					)}

					{/* Search bar - only shown when searchBarVisible is true */}
					{searchBarVisible && (
						<div className={classes['topNav-content-search']}>
							<SearchBar query={query} setQuery={setQuery} placeholder="Search..." />
						</div>
					)}
				</div>

				{/* Right section with user info and actions */}
				<div className={classes['topNav-right']}>
					{/* User profile section */}
					<div className={classes['topNav-userInfo']} onClick={() => push('/profile')}>
						{/* User avatar */}
						<Image
							className={classes['topNav-userInfo-avatar']}
							alt="avatar"
							src={me.avatar ?? 'plAvatar'} // Use placeholder avatar if user has none
						/>
						<div className={classes['topNav-userInfo-detail']}>
							{/* User name */}
							<Typography className={classes['topNav-userInfo-name']} variant="h5">
								{me.name}
							</Typography>
							{/* User role */}
							<Typography className={classes['topNav-userInfo-role']} variant="h6">
								{me.roleId === 1 ? 'Super Administrator' : 'Administrator'}
							</Typography>
						</div>
					</div>

					{/* Notification bell - only shown when notificationVisible is true */}
					{notificationVisible && (
						<a className={classes['topNav-notification']}>
							<Image src={'icBellPrimary'} />
						</a>
					)}

					{/* Mobile menu button - only shown on small devices */}
					{isSmallDevice && (
						<a className={classes['topNav-menuButton']} onClick={openMenu}>
							<Image src={'icMenu'} />
						</a>
					)}
				</div>
			</div>
		</div>
	);
};

export default TopNav;
